/**  @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as React from 'react';

import { Paragraph } from '@travelnest/merlin.typography';
import {
	CalendarContainer,
	Container,
	Day,
	DayHeader,
	DaysOfWeekContainer,
	getCellStyling,
	OverridePrice,
	Price,
	Prices,
} from './Calendar.styled';
import { CalendarEvent } from './CalendarEvent';
import { FIRST_DAY_OF_WEEK } from './config';
import {
	dateEquals,
	dateIsInRange,
	dayRange,
	eventFinishesDateAvailable,
	getDayCellAdditionalBorders,
	getFirstDayOfMonth,
	getFirstDayOfWeek,
	getLastDayOfMonth,
	getLastDayOfWeek,
	utcNormalisedDate,
} from './date-helpers';
import { expandEvent, monthlyEventFilter, monthlyPriceFilter, trimAndConvertToBlock } from './event-manipulation';
import { ICalendarDayProps, ICalendarProps } from './types';

const HEADERS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toggleDaySelection = (selectedDates: Date[], date: Date): Date[] => {
	const idx = selectedDates.findIndex((selected) => dateEquals(selected, date));
	if (idx > -1) {
		return [...selectedDates.slice(0, idx), ...selectedDates.slice(idx + 1)];
	} else {
		return [...selectedDates, date];
	}
};

const CalendarDay: React.FC<ICalendarDayProps> = (props) => {
	const { day, current, selectedDates, pricesForNight, onSelectionToggle, additionalBorder, eventsToday } = props;
	const overspill = day.getMonth() !== current.getMonth();
	const styling = getCellStyling(eventsToday, selectedDates, day, overspill);

	return (
		<Day
			css={css`${styling}`}
			tabIndex={overspill ? undefined : 0}
			onKeyPress={(ev) => ev.key === 'Enter' && !overspill && onSelectionToggle(day)}
			onClick={() => !overspill && onSelectionToggle(day)}
			overspill={overspill}
			additionalBorder={additionalBorder}
			selected={!!selectedDates.find((d) => dateEquals(d, day))}
		>
			{!overspill && day.getDate()}
			{!overspill && eventFinishesDateAvailable(eventsToday, day) && (
				<Prices>
					{pricesForNight?.override && <OverridePrice>{pricesForNight?.override}</OverridePrice>}
					<Price overrideExists={!!pricesForNight?.override}>{pricesForNight?.price}</Price>
				</Prices>
			)}
		</Day>
	);
};

export const Calendar: React.FC<ICalendarProps> = ({
	events,
	nightlyPrices,
	current,
	selectedDates,
	onDateSelectionChange,
	goToBooking,
	onSyncedOrBlockedEventClick
}) => {
	const selectionHandler = (date: Date, nextDates: Date[]) => {
		if (date.getMonth() !== current.getMonth()) return; // can only select dates within current month
		onDateSelectionChange(nextDates);
	};

	const start = getFirstDayOfWeek(getFirstDayOfMonth(current));
	const end = getLastDayOfWeek(getLastDayOfMonth(current));
	const days = dayRange(start, end);
	// Day cells are given bottom/right borders by default, this is an array of which of top/left they also need.
	const dayCellBorders = getDayCellAdditionalBorders(current.getMonth(), days);

	const filterToMonth = monthlyEventFilter(current);
	const trimAndConvert = trimAndConvertToBlock(current);

	const filterPriceToMonth = monthlyPriceFilter(current);

	const eventsToBeRendered = (events || [])
		// filter to events happening this month
		.filter(filterToMonth)
		// expand event blocks that overlap multiple weeks
		.reduce(expandEvent, [])
		// once events have been split we need to then filter to get the ones in current month only
		.filter(filterToMonth)
		// trim overhanging dates and convert to event block format for rendering
		.map(trimAndConvert);

	// prices filtered down to only those within the current month
	const pricesToBeRendered = (nightlyPrices || []).filter(filterPriceToMonth);

	return (
		<Container>
			<DaysOfWeekContainer>
				{
					HEADERS.map((header, i) => {
						const displayHeader = HEADERS[(FIRST_DAY_OF_WEEK + i) % HEADERS.length];
						return (
							<DayHeader
								key={header + i}>
								<Paragraph>{displayHeader.slice(0, 1)}</Paragraph>
							</DayHeader>
						);
					}
					)
				}
			</DaysOfWeekContainer>
			<CalendarContainer>
				{days.map((day, i) => {
					day = utcNormalisedDate(day);
					const eventsToday = events?.filter((e) => dateIsInRange(day, [e.start, e.end]));
					const pricesForNight = pricesToBeRendered.find((np) => dateEquals(np.date, day));

					return <CalendarDay key={`${current.getMonth()}-${current.getFullYear()}-${i}`}
						day={day}
						additionalBorder={dayCellBorders[i]}
						current={current}
						selectedDates={selectedDates}
						pricesForNight={pricesForNight}
						eventsToday={eventsToday || []}
						onSelectionToggle={(selected) => selectionHandler(selected, toggleDaySelection(selectedDates, selected))}
					/>;
				})}
				{eventsToBeRendered.map((ev, i) => {
					const contiguousEvent = !!eventsToBeRendered.find((event) => dateEquals(event.end, ev.start) && event.event.id !== ev.event.id);
					return (
						<CalendarEvent
							key={`${i}-${ev.event.id}-${ev.event.start}`}
							event={ev}
							goToBooking={goToBooking}
							contiguousEvent={contiguousEvent}
							onSyncedEventClick={onSyncedOrBlockedEventClick}
						/>
					);
				})}
			</CalendarContainer>
		</Container >
	);
};
